import { Gift, Image, Loader2, Search, SendHorizonal, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { giphyFetch, pickGiphyBestUrl } from "../../../utils/messages/gifHelpers";
import {
  clubThreadGifPickerInsetClass,
  clubThreadGifPickerPanelClass,
} from "../presentation/threadPresentation.js";

/** Search hits + actions live outside this input; blur must not close search when focus moves here. */
const CLUB_FULL_COMMENT_SEARCH_SURFACE = "[data-club-full-comment-search]";

/** Above club thread comment siblings (e.g. z-35 cards); below image lightbox (~z-[10060]). */
const CLUB_THREAD_GIF_PORTAL_Z = "z-[10050]";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

export default function ThreadComposer({
  viewerId,
  canParticipate,
  replyText,
  sending,
  onReplyTextChange,
  onSubmit,
  className = "",
  collapsible = false,
  collapsedPlaceholder = "Join the conversation",
  /** When false, composer is text-only. */
  allowAttachments = true,
  /**
   * With allowAttachments: 'full' = image + GIF (clubs); 'imageOnly' = image upload only, no GIF picker.
   */
  attachmentPolicy = "full",
  /** Shown when viewer is signed in but cannot participate (e.g. club-only). */
  restrictedParticipantHint,
  isSearchMode = false,
  searchText = "",
  onSearchTextChange,
  onExitSearchMode,
  /** Focus target after explicit search exit (Escape / close control); not used for blur-based exit. */
  searchFocusReturnRef = null,
  autoFocus = false,
  showCancelButton = false,
  onCancel,
}) {
  const [expanded, setExpanded] = useState(!collapsible);
  const prevSendingRef = useRef(Boolean(sending));
  const textareaRef = useRef(null);
  const searchA11yDescId = useId();

  // Media draft state (GIF URL + image upload)
  const [gifPickerOpen, setGifPickerOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifItems, setGifItems] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [gifError, setGifError] = useState("");
  const gifAbortRef = useRef(null);
  const gifPanelRef = useRef(null);
  const gifTriggerWrapRef = useRef(null);
  const [gifPortalPos, setGifPortalPos] = useState({ top: 0, left: 0, gridMaxH: 360 });

  const [gifUrl, setGifUrl] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const hasAttachment = Boolean(gifUrl || imageFile || imagePreviewUrl);
  const disableAddMedia = hasAttachment; // exactly one attachment max (reddit-like)

  // Enforce state exclusivity (server also enforces, but this keeps UX consistent).
  useEffect(() => {
    if (!gifUrl) return;
    if (imageFile || imagePreviewUrl) {
      setImageFile(null);
      setImagePreviewUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gifUrl]);

  useEffect(() => {
    if (!imagePreviewUrl && !imageFile) return;
    if (gifUrl) {
      setGifUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreviewUrl, imageFile]);

  useEffect(() => {
    if (hasAttachment) setGifPickerOpen(false);
  }, [hasAttachment]);
  const imageInputId = useMemo(
    () => `clubThreadImageInput_${Math.random().toString(36).slice(2)}`,
    []
  );

  const policy = useMemo(
    () => (allowAttachments ? attachmentPolicy : "none"),
    [allowAttachments, attachmentPolicy]
  );

  const imageInputAccept = useMemo(
    () =>
      policy === "imageOnly"
        ? "image/jpeg,image/png,image/webp"
        : "image/jpeg,image/png,image/webp,image/gif",
    [policy]
  );

  useEffect(() => {
    if (policy === "imageOnly" && gifUrl) {
      setGifUrl("");
      setGifPickerOpen(false);
    }
  }, [policy, gifUrl]);

  const effectiveShowCancel = Boolean(collapsible || showCancelButton);
  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    else if (collapsible) setExpanded(false);
  };

  const textNonEmpty = Boolean(String(replyText || "").trim());
  const canSubmit =
    policy === "none"
      ? textNonEmpty
      : policy === "imageOnly"
        ? textNonEmpty || Boolean(imageFile)
        : textNonEmpty || Boolean(gifUrl) || Boolean(imageFile);

  useEffect(() => {
    if (!collapsible) return;
    if (String(replyText || "").trim()) setExpanded(true);
  }, [collapsible, replyText]);

  useEffect(() => {
    if (!collapsible) return;
    const prevSending = prevSendingRef.current;
    if (prevSending && !sending && !String(replyText || "").trim()) {
      setExpanded(false);
    }
    prevSendingRef.current = Boolean(sending);
  }, [collapsible, sending, replyText]);

  useEffect(() => {
    if (!autoFocus || !viewerId || !canParticipate || isSearchMode) return;
    if (collapsible) setExpanded(true);
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }, [autoFocus, viewerId, canParticipate, collapsible, isSearchMode]);

  const openComposer = () => {
    if (!collapsible) return;
    setExpanded(true);
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  };

  useLayoutEffect(() => {
    if (!gifPickerOpen) return;
    const wrap = gifTriggerWrapRef.current;
    if (!wrap) return;

    const updatePos = () => {
      const r = wrap.getBoundingClientRect();
      const margin = 12;
      const width = Math.min(360, window.innerWidth - margin * 2);
      let left = r.left;
      if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - width);
      }
      const top = r.bottom + 8;
      const availBelow = window.innerHeight - top - margin;
      const gridMaxH = Math.min(360, Math.max(140, availBelow));
      setGifPortalPos({ top, left, gridMaxH });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [gifPickerOpen]);

  useEffect(() => {
    if (!gifPickerOpen) return;

    const onDocPointerDown = (e) => {
      const t = e.target;
      if (t instanceof Node && gifPanelRef.current?.contains(t)) return;
      if (t instanceof Node && gifTriggerWrapRef.current?.contains(t)) return;
      setGifPickerOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [gifPickerOpen]);

  useEffect(() => {
    if (!gifPickerOpen) return;

    const key = process.env.REACT_APP_GIPHY_KEY;
    if (!key) {
      setGifItems([]);
      setGifError("GIF picker not configured (missing GIPHY key).");
      setGifLoading(false);
      return;
    }

    const t = window.setTimeout(async () => {
      setGifLoading(true);
      setGifError("");
      try {
        if (gifAbortRef.current) gifAbortRef.current.abort();
        const controller = new AbortController();
        gifAbortRef.current = controller;

        const q = String(gifQuery || "").trim();
        const endpoint = q ? "search" : "trending";
        const params = q
          ? { q, limit: 24, rating: "pg-13" }
          : { limit: 24, rating: "pg-13" };

        const items = await giphyFetch({
          key,
          endpoint,
          params,
          signal: controller.signal,
        });

        setGifItems(Array.isArray(items) ? items : []);
      } catch (e) {
        if (e?.name !== "AbortError") {
          setGifError(e?.message || "Failed to load GIFs.");
          setGifItems([]);
        }
      } finally {
        setGifLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(t);
  }, [gifPickerOpen, gifQuery]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
      if (gifAbortRef.current) gifAbortRef.current.abort();
    };
  }, [imagePreviewUrl]);

  const resetMediaDraft = () => {
    setGifUrl("");
    setGifQuery("");
    setGifItems([]);
    setGifPickerOpen(false);
    setGifError("");
    setImageFile(null);
    setImagePreviewUrl("");
  };

  const removeAttachment = () => {
    setGifUrl("");
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImageFile(null);
    setImagePreviewUrl("");
  };

  const handleSubmitClick = async () => {
    if (sending) return;
    if (!canSubmit) return;

    const payload = {
      text: String(replyText || ""),
      gifUrl: policy === "full" ? gifUrl || null : null,
      file: imageFile || null,
    };

    if (process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1") {
      // eslint-disable-next-line no-console
      console.info("[ThreadComposer submit]", {
        submitPayload: payload,
        expectedTransport: payload.file ? "multipart" : "json",
        textLen: payload.text.trim().length,
        hasGif: Boolean(payload.gifUrl),
        gifSample: payload.gifUrl ? String(payload.gifUrl).slice(0, 72) : null,
        hasFile: Boolean(payload.file),
        fileMeta: payload.file
          ? { name: payload.file.name, size: payload.file.size, type: payload.file.type }
          : null,
      });
    }

    try {
      await onSubmit?.(payload);
      resetMediaDraft();
    } catch {
      // Parent should handle error display; keep the draft so user can retry.
    }
  };

  if (isSearchMode) {
    const focusSearchTrigger = () => {
      requestAnimationFrame(() => {
        const el = searchFocusReturnRef?.current;
        if (el && typeof el.focus === "function") {
          try {
            el.focus();
          } catch {
            /* ignore */
          }
        }
      });
    };

    const exitSearchExplicit = () => {
      onExitSearchMode?.();
      focusSearchTrigger();
    };

    return (
      <div
        className={cn(
          "px-3 sm:px-5 md:px-6 py-5 border-b border-white/10 w-full min-w-0 max-w-full box-border",
          className
        )}
      >
        <p id={searchA11yDescId} className="sr-only">
          Press Escape to close search and return to Search Comments.
        </p>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" aria-hidden />
          <input
            autoFocus
            autoComplete="off"
            value={searchText}
            onChange={(e) => onSearchTextChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                exitSearchExplicit();
              }
            }}
            onBlur={(e) => {
              const rt = e.relatedTarget;
              if (rt instanceof Element && rt.closest?.(CLUB_FULL_COMMENT_SEARCH_SURFACE)) return;
              requestAnimationFrame(() => {
                const ae = document.activeElement;
                if (ae instanceof Element && ae.closest?.(CLUB_FULL_COMMENT_SEARCH_SURFACE)) return;
                onExitSearchMode?.();
              });
            }}
            placeholder="Search comments"
            aria-describedby={searchA11yDescId}
            className="w-full h-11 rounded-xl border border-white/12 bg-black/20 pl-9 pr-10 text-sm text-gray-100 placeholder:text-gray-500 outline-none focus:border-white/20"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exitSearchExplicit()}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/[0.06] inline-flex items-center justify-center transition-colors"
            aria-label="Close search"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "px-3 sm:px-5 md:px-6 py-5 border-b border-white/10 w-full min-w-0 max-w-full box-border",
        className
      )}
    >
      {!viewerId ? (
        <>
          <p className="text-sm font-semibold text-gray-100">Add a comment</p>
          <p className="mt-2 text-sm text-gray-500">Log in to join the discussion.</p>
        </>
      ) : !canParticipate ? (
        <>
          <p className="text-sm font-semibold text-gray-100">Add a comment</p>
          <p className="mt-2 text-sm text-amber-200/80">
            {restrictedParticipantHint || "Join this club to post comments."}
          </p>
        </>
      ) : collapsible && !expanded ? (
        <button
          type="button"
          onClick={openComposer}
          className="w-full h-11 rounded-xl border border-white/10 bg-white/[0.02] px-4 text-left text-sm text-gray-400 hover:bg-white/[0.05] hover:text-gray-300 transition-colors"
        >
          {collapsedPlaceholder}
        </button>
      ) : (
        <>
          <p className="text-sm font-semibold text-gray-100 mb-2">
            {collapsible ? "Join the conversation" : "Add a comment"}
          </p>

          {/* Single Reddit-style draft surface: one border; media (if any) then text in the same box */}
          <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden focus-within:border-white/20 transition-colors">
            {policy !== "none" && (gifUrl || imagePreviewUrl) ? (
              <div className="relative px-3 sm:px-4 pt-3 pb-2 border-b border-white/10 bg-black/20">
                <img
                  src={gifUrl || imagePreviewUrl}
                  alt={gifUrl ? "Selected GIF" : "Selected image"}
                  className="w-full max-h-[220px] sm:max-h-[260px] object-contain rounded-lg bg-black/30"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={removeAttachment}
                  className="absolute top-2 right-2 sm:top-2.5 sm:right-3 h-8 w-8 rounded-full border border-white/20 bg-black/70 text-white hover:bg-black/90 grid place-items-center shadow-sm"
                  aria-label="Remove attachment"
                  title="Remove attachment"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : null}

            <textarea
              ref={textareaRef}
              value={replyText}
              onChange={(e) => onReplyTextChange(e.target.value)}
              placeholder="What do you think?"
              className="w-full min-h-[100px] sm:min-h-[120px] resize-none bg-transparent px-3 sm:px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 outline-none border-0 ring-0 focus:ring-0"
              maxLength={2000}
            />
          </div>

          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              {policy !== "none" ? (
                <>
                  <input
                    id={imageInputId}
                    type="file"
                    accept={imageInputAccept}
                    className="sr-only"
                    aria-hidden
                    tabIndex={-1}
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      // allow re-selecting the same file
                      e.target.value = "";
                      if (!f) return;
                      if (disableAddMedia) return; // one attachment max
                      setImageFile(f);
                      setImagePreviewUrl(URL.createObjectURL(f));
                    }}
                  />

                  <label
                    htmlFor={imageInputId}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-white/7 bg-transparent text-[11px] sm:text-xs text-gray-300 hover:bg-white/[0.05] transition-colors cursor-pointer"
                    aria-label="Add image"
                    title="Add image"
                    onClick={(e) => {
                      if (disableAddMedia) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    style={disableAddMedia ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                  >
                    <Image className="h-3.5 w-3.5" />
                    Image
                  </label>

                  {policy === "full" ? (
                  <div ref={gifTriggerWrapRef} className="relative inline-flex">
                    <button
                      type="button"
                      onClick={() => {
                        if (disableAddMedia) return;
                        setGifPickerOpen((v) => !v);
                      }}
                      disabled={disableAddMedia}
                      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-white/7 bg-transparent text-[11px] sm:text-xs text-gray-300 hover:bg-white/[0.05] transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                      aria-label="Open GIF picker"
                      title="GIF"
                    >
                      <Gift className="h-3.5 w-3.5" />
                      GIF
                    </button>

                    {typeof document !== "undefined" &&
                      gifPickerOpen &&
                      createPortal(
                    <div
                      ref={gifPanelRef}
                      className={cn(
                        "fixed box-border w-[min(360px,calc(100vw-1.5rem))] pointer-events-auto",
                        CLUB_THREAD_GIF_PORTAL_Z,
                        clubThreadGifPickerPanelClass
                      )}
                      style={{
                        top: gifPortalPos.top,
                        left: gifPortalPos.left,
                      }}
                      role="dialog"
                      aria-modal="true"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={gifQuery}
                          onChange={(e) => setGifQuery(e.target.value)}
                          placeholder="Search GIFs..."
                          className={cn(
                            "flex-1 rounded-xl px-3 py-2 text-[13px] placeholder:text-gray-500 outline-none focus:border-white/22 focus:ring-2 focus:ring-white/10",
                            clubThreadGifPickerInsetClass
                          )}
                        />
                        <button
                          type="button"
                          className={cn(
                            "h-9 w-9 shrink-0 rounded-full grid place-items-center transition-colors hover:bg-[#0c121c]",
                            clubThreadGifPickerInsetClass
                          )}
                          onClick={() => setGifPickerOpen(false)}
                          aria-label="Close GIF picker"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {gifError ? (
                        <div className="rounded-xl border border-red-500/25 bg-[#1a0a0c] px-3 py-2 text-[12px] text-red-200">
                          {gifError}
                        </div>
                      ) : gifLoading ? (
                        <div className="rounded-xl border border-white/[0.08] bg-[#050810] px-3 py-6 text-center text-[12px] text-gray-400">
                          Loading GIFs...
                        </div>
                      ) : gifItems.length ? (
                        <div
                          className="rounded-xl border border-white/[0.08] bg-[#050810] p-2 overflow-y-auto overscroll-contain"
                          style={{ maxHeight: gifPortalPos.gridMaxH }}
                        >
                          <div className="grid grid-cols-3 gap-2">
                            {gifItems.map((item) => {
                              const url = pickGiphyBestUrl(item);
                              if (!url) return null;
                              return (
                                <button
                                  key={item?.id || url}
                                  type="button"
                                  onClick={() => {
                                    if (disableAddMedia) return;
                                    setGifUrl(url);
                                    setGifPickerOpen(false);
                                    setGifQuery("");
                                  }}
                                  className={cn(
                                    "relative aspect-square overflow-hidden rounded-lg p-0 hover:border-white/22 transition",
                                    clubThreadGifPickerInsetClass
                                  )}
                                  aria-label="Select GIF"
                                >
                                  <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-white/[0.08] bg-[#050810] px-3 py-6 text-center text-[12px] text-gray-400">
                          No GIFs found
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
                  </div>
                  ) : null}
                </>
              ) : null}
            </div>

            <p className="text-[10px] text-gray-500">{replyText.length}/2000</p>
            <div className="flex items-center gap-2 ml-auto">
              {effectiveShowCancel ? (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center h-9 px-3 rounded-xl border border-white/10 bg-white/[0.02] text-xs font-semibold text-gray-300 hover:bg-white/[0.06] transition-colors"
                >
                  Cancel
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSubmitClick}
                disabled={!canSubmit || sending}
                className={cn(
                  "inline-flex items-center gap-2 h-9 px-4 rounded-xl border text-xs font-semibold transition-colors",
                  canSubmit
                    ? "border-white/12 bg-white/[0.08] text-gray-100 hover:bg-white/[0.14]"
                    : "border-white/8 bg-white/[0.03] text-gray-500 cursor-not-allowed"
                )}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SendHorizonal className="h-4 w-4" />
                )}
                Comment
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
